import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "um_chat_session_v1";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [tier, setTier] = useState("free");
  const [labelDismissed, setLabelDismissed] = useState(false);
  const scrollRef = useRef(null);

  // Restore prior session
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.session_id) {
          setSessionId(saved.session_id);
          setMessages(saved.messages || []);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Auto-open from ?chat=open URL param (used for IG bio link, etc.)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const flag = params.get("chat");
      if (flag === "open" || flag === "1" || flag === "true") {
        setOpen(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Listen for global "open chat" events fired by hero buttons elsewhere
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-um-chat", handler);
    return () => window.removeEventListener("open-um-chat", handler);
  }, []);

  // Phone "Ask our AI" label: dismiss after a scroll or after the user opens
  // the chat once. Sticky across page navs in the same session.
  useEffect(() => {
    if (sessionStorage.getItem("um_chat_label_dismissed") === "1") {
      setLabelDismissed(true);
      return;
    }
    const onScroll = () => {
      if (window.scrollY > 120) {
        setLabelDismissed(true);
        sessionStorage.setItem("um_chat_label_dismissed", "1");
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      setLabelDismissed(true);
      sessionStorage.setItem("um_chat_label_dismissed", "1");
    }
  }, [open]);

  useEffect(() => {
    if (!sessionId) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ session_id: sessionId, messages })
    );
  }, [sessionId, messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const isCaptured = !!sessionId;

  async function handleStart(e) {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) return;
    setStarting(true);
    try {
      const r = await fetch(`${API}/chat/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      if (!r.ok) throw new Error("start_failed");
      const data = await r.json();
      setSessionId(data.session_id);
      setTier(data.tier || "free");
      setMessages([{ role: "assistant", text: data.greeting }]);
    } catch {
      setError("Couldn't start the chat. Try again in a moment.");
    } finally {
      setStarting(false);
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending || !sessionId) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const r = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!r.ok) throw new Error("send_failed");
      const data = await r.json();
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.tier) setTier(data.tier);
      if (data.limit_reached) {
        setLimitReached(true);
      }
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Hmm — I hit a snag. Try that again?",
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {/* Floating bubble + "Ask our AI" label (all screen sizes) */}
      <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2">
        {!open && !labelDismissed ? (
          <motion.button
            type="button"
            data-testid="chat-bubble-label"
            aria-label="Open sync chat"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.8, duration: 0.4 }}
            className="bg-ink-900/95 backdrop-blur border border-amber/60 hover:border-amber text-white px-4 py-3 rounded-full shadow-[0_8px_24px_-8px_rgba(34,197,94,0.6)] whitespace-nowrap flex items-center gap-2 group transition-colors"
          >
            <span className="font-display text-xs uppercase tracking-widest text-amber">
              Ask Our AI
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/55 group-hover:text-white">
              Sync Expert →
            </span>
          </motion.button>
        ) : null}

        <button
          type="button"
          data-testid="chat-bubble-toggle"
          aria-label="Open sync chat"
          onClick={() => setOpen((v) => !v)}
          className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber text-black shadow-[0_10px_30px_-10px_rgba(34,197,94,0.6)] hover:bg-amber-hover transition-colors"
        >
          {!open && !labelDismissed ? (
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-amber/60 animate-ping"
            />
          ) : null}
          <span className="relative">
            {open ? <X size={24} /> : <MessageCircle size={24} />}
          </span>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-5 z-50 w-[min(380px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-8rem))] bg-ink-900 border border-ink-600 rounded-lg shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-ink-600 bg-ink-800">
              <p className="font-display text-sm uppercase tracking-widest text-amber">
                Ask the Underground Mix Team
              </p>
              <p className="text-xs text-white/55 mt-0.5">
                Real sync answers — not theory.
              </p>
            </div>

            {/* Body */}
            {!isCaptured ? (
              <form
                data-testid="chat-capture-form"
                onSubmit={handleStart}
                className="flex-1 p-5 flex flex-col gap-4 justify-center"
              >
                <div>
                  <h3 className="font-display text-xl font-bold text-white leading-tight">
                    Get real sync answers.
                  </h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">
                    Drop your name and email and ask anything about sync
                    licensing, song craft, publishing, or how Underground Mix
                    works.
                  </p>
                </div>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    Name
                  </span>
                  <input
                    data-testid="chat-input-name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full bg-ink-800 border border-ink-600 focus:border-amber rounded-sm px-3 py-2 text-sm text-white outline-none"
                    placeholder="Your first name"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                    Email
                  </span>
                  <input
                    data-testid="chat-input-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full bg-ink-800 border border-ink-600 focus:border-amber rounded-sm px-3 py-2 text-sm text-white outline-none"
                    placeholder="you@example.com"
                  />
                </label>
                {error ? (
                  <p className="text-xs text-red-400">{error}</p>
                ) : null}
                <button
                  type="submit"
                  data-testid="chat-start-cta"
                  disabled={starting}
                  className="w-full bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-xs px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
                >
                  {starting ? "Connecting…" : "Start Chatting"}
                </button>
                <p className="text-[10px] text-white/40 font-mono uppercase tracking-widest text-center">
                  No spam. Unsubscribe anytime.
                </p>
              </form>
            ) : (
              <>
                <div
                  ref={scrollRef}
                  data-testid="chat-message-list"
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
                >
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      data-testid={`chat-msg-${m.role}`}
                      className={`flex ${
                        m.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[85%] text-sm leading-relaxed px-3 py-2 rounded-md whitespace-pre-wrap ${
                          m.role === "user"
                            ? "bg-amber text-black"
                            : "bg-ink-800 text-white/90 border border-ink-600"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                  {sending ? (
                    <div className="flex justify-start">
                      <div className="bg-ink-800 border border-ink-600 px-3 py-2 rounded-md text-sm text-white/60 italic">
                        Typing…
                      </div>
                    </div>
                  ) : null}
                </div>

                {limitReached ? (
                  <div
                    data-testid="chat-limit-cta"
                    className="border-t border-ink-600 p-4 bg-ink-800 space-y-3"
                  >
                    {tier === "premium" || tier === "plus" ? (
                      <>
                        <p className="text-xs text-white/65 leading-relaxed text-center">
                          You're out of questions for this 3-month window —
                          they refill at the start of the next quarter. Want
                          answers sooner? Book a 1:1:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <a
                            href="https://calendly.com/underground-mix/30-min-1-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="chat-limit-cta-30"
                            className="text-center bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-[10px] px-2 py-2.5 rounded-sm transition-colors"
                          >
                            30-min 1:1
                          </a>
                          <a
                            href="https://calendly.com/underground-mix/60-min-1-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="chat-limit-cta-60"
                            className="text-center bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-[10px] px-2 py-2.5 rounded-sm transition-colors"
                          >
                            60-min 1:1
                          </a>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-white/65 leading-relaxed text-center">
                          You've used your 10 free questions — no reset. Want
                          deeper feedback and ongoing bot access?
                        </p>
                        <a
                          href="/access"
                          data-testid="chat-limit-cta-access"
                          className="block w-full text-center bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-xs px-4 py-3 rounded-sm transition-colors"
                        >
                          Underground ACCESS · From $39 / mo
                        </a>
                      </>
                    )}
                  </div>
                ) : (
                  <form
                    onSubmit={handleSend}
                    className="border-t border-ink-600 p-3 flex items-center gap-2 bg-ink-800"
                  >
                    <input
                      data-testid="chat-input-message"
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Ask about sync…"
                      disabled={sending}
                      className="flex-1 bg-ink-900 border border-ink-600 focus:border-amber rounded-sm px-3 py-2 text-sm text-white outline-none disabled:opacity-60"
                    />
                    <button
                      type="submit"
                      data-testid="chat-send-cta"
                      disabled={sending || !draft.trim()}
                      aria-label="Send"
                      className="h-9 w-9 inline-flex items-center justify-center bg-amber hover:bg-amber-hover text-black rounded-sm disabled:opacity-50 transition-colors"
                    >
                      <Send size={16} />
                    </button>
                  </form>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
